import { Invoice, InvoiceUpdateData, lineItemType } from "../../../../../pojo/Pricing";
import { S25Util } from "../../../../../util/s25-util";
import { S25DateParse } from "../../../../../util/core/s25.date.parse";
import { emptyReservationListFields, InvoiceCreateData } from "../../s25.event.pricing.service";
import { InvoiceMove } from "../move-item/invoice-move";
import { DEFAULT_SET_DISPLAY_NAME, invoiceDisplayName } from "../../s25.event.pricing.store";
import { isDefaultSet } from "../system-sets/system-sets";

export namespace InvoiceForm {
    /** How an invoice decides which occurrences it bills. */
    export type OccurrenceBillingMode = "list" | "date" | "all";

    export type DateRange = { start: Date; end: Date };

    const INCLUDE_ALL_START_YEAR = 1900;
    const INCLUDE_ALL_END_YEAR = 2100;

    /** A range wide enough that every occurrence the event will ever have falls inside it. */
    export function allOccurrencesRange(): DateRange {
        return { start: new Date(`1/1/${INCLUDE_ALL_START_YEAR}`), end: new Date(`1/1/${INCLUDE_ALL_END_YEAR}`) };
    }

    /**
     * The year the API states a bill definition date in, read off the string it sent rather than a
     * parsed Date. S25Util.date.parse hands a date more than twenty years old whose year the string does
     * not literally contain to the current year, which is what a UTC-stated 1900 becomes: it lands on
     * 1899 in a negative offset zone, and comes back stamped with this year.
     */
    function statedYear(value: Date | string | undefined): number | undefined {
        const stated = String(value ?? "").match(/^\d{4}/)?.[0];
        if (stated) return Number(stated);

        const parsed = S25Util.date.parse(value)?.getFullYear();
        return Number.isFinite(parsed) ? parsed : undefined;
    }

    /**
     * The API has no "include all" flag, so an invoice created that way is one spanning 1900 to 2100.
     * Either end says so on its own, since no range a user picks reaches either.
     */
    export function isIncludeAllInvoice(invoice: Invoice | undefined) {
        const { startDt, endDt } = invoice?.billDefn ?? {};
        if (!startDt && !endDt) return false;

        return (
            (statedYear(startDt) ?? Infinity) <= INCLUDE_ALL_START_YEAR ||
            (statedYear(endDt) ?? -Infinity) >= INCLUDE_ALL_END_YEAR
        );
    }

    export function invoiceBillingMode(invoice: Invoice | undefined): OccurrenceBillingMode {
        if (isIncludeAllInvoice(invoice)) return "all";
        if (invoice?.billDefn?.startDt && invoice?.billDefn?.endDt) return "date";
        return "list";
    }

    export function invoiceDateRange(invoice: Invoice | undefined): DateRange | undefined {
        const { startDt, endDt } = invoice?.billDefn ?? {};
        if (!startDt || !endDt) return undefined;
        return { start: S25DateParse.parseDropTZLenient(startDt), end: S25DateParse.parseDropTZLenient(endDt) };
    }

    export function isRangeInverted(range: DateRange | undefined) {
        if (!range?.start || !range?.end) return false;
        return S25Util.date.parse(range.start) > S25Util.date.parse(range.end);
    }

    export type OccurrenceLike = {
        rsrvId: number;
        rsrvStartDt?: string | Date;
        rsrvEndDt?: string | Date;
    };

    export function occurrencesInRange<T extends OccurrenceLike>(occurrences: T[], range: DateRange | undefined): T[] {
        if (!range?.start || !range?.end || isRangeInverted(range)) return [];

        const rangeStart = S25Util.date.toStartOfDay(S25Util.date.parse(range.start));
        const rangeEnd = S25Util.date.toEndOfDay(S25Util.date.parse(range.end));

        return occurrences.filter((occurrence) => {
            if (!occurrence.rsrvStartDt || !occurrence.rsrvEndDt) return false;
            return (
                S25DateParse.parseDropTZLenient(occurrence.rsrvStartDt) < rangeEnd &&
                S25DateParse.parseDropTZLenient(occurrence.rsrvEndDt) > rangeStart
            );
        });
    }

    export type BilledToInvoice = { invoiceId: number; invoiceName: string; isDefaultSet: boolean };

    export function billedToLabel(
        billedTo: BilledToInvoice | undefined,
        exemptInvoiceIds: ReadonlySet<number>,
    ): string {
        if (!billedTo) return DEFAULT_SET_DISPLAY_NAME;
        if (billedTo.isDefaultSet || exemptInvoiceIds.has(billedTo.invoiceId)) return billedTo.invoiceName;
        return `${billedTo.invoiceName} (cannot be selected)`;
    }

    /** Whether another invoice has taken the item, which is what a new invoice cannot bill. */
    export function isClaimed(billedTo: BilledToInvoice | undefined): billedTo is BilledToInvoice {
        return !!billedTo && !billedTo.isDefaultSet;
    }

    /** Invoices whose billing does not stand in the way, ie the one being edited or moved off of. */
    type BillingScope = {
        invoices: Invoice[];
        exemptInvoiceIds?: Iterable<number>;
        eventId: number | undefined;
        combineRelated: boolean;
    };

    function billingScope(args: BillingScope) {
        const exempt = new Set(args.exemptInvoiceIds ?? []);
        return {
            relevantInvoices: args.invoices.filter((invoice) => !exempt.has(invoice.id)),
            billsThisView: (lineItem: { eventId?: number }) => args.combineRelated || lineItem.eventId === args.eventId,
        };
    }

    /**
     * Which invoice bills each occurrence. Event type line items are excluded because they are billed
     * per event rather than per occurrence, and other events' line items only count when the view
     * combines related events.
     */
    export function occurrenceBillingByRsrvId(args: BillingScope): Map<number, BilledToInvoice> {
        const { relevantInvoices, billsThisView } = billingScope(args);
        const billing = new Map<number, BilledToInvoice>();

        for (const invoice of relevantInvoices) {
            for (const lineItem of invoice.billing?.lineItems ?? []) {
                if (lineItem.itemType === lineItemType.EVENT) continue;
                if (!billsThisView(lineItem)) continue;
                for (const occurrence of lineItem.occurrences ?? []) {
                    if (billing.has(occurrence.rsrvId)) continue;
                    billing.set(occurrence.rsrvId, {
                        invoiceId: invoice.id,
                        invoiceName: invoiceDisplayName(invoice),
                        isDefaultSet: isDefaultSet(invoice),
                    });
                }
            }
        }

        return billing;
    }

    /** Which invoice bills each requirement, on the same terms as the occurrence billing map. */
    export function requirementBillingByRequirementId(args: BillingScope): Map<number, BilledToInvoice> {
        const { relevantInvoices, billsThisView } = billingScope(args);
        const billing = new Map<number, BilledToInvoice>();

        for (const invoice of relevantInvoices) {
            for (const lineItem of invoice.billing?.lineItems ?? []) {
                if (lineItem.itemType !== lineItemType.REQUIREMENT || !lineItem.itemId) continue;
                if (!billsThisView(lineItem)) continue;
                if (billing.has(lineItem.itemId)) continue;
                billing.set(lineItem.itemId, {
                    invoiceId: invoice.id,
                    invoiceName: invoiceDisplayName(invoice),
                    isDefaultSet: isDefaultSet(invoice),
                });
            }
        }

        return billing;
    }

    export type OccurrenceConflict = BilledToInvoice & { rsrvId: number; label: string };

    export function conflictingOccurrences(
        args: BillingScope & {
            candidateRsrvIds: Iterable<number>;
            labels?: ReadonlyMap<number, string>;
        },
    ): OccurrenceConflict[] {
        const billing = occurrenceBillingByRsrvId(args);
        const conflicts: OccurrenceConflict[] = [];

        for (const rsrvId of args.candidateRsrvIds) {
            const billedTo = billing.get(rsrvId);
            if (!isClaimed(billedTo)) continue;
            conflicts.push({ rsrvId, label: args.labels?.get(rsrvId) ?? "", ...billedTo });
        }

        return conflicts;
    }

    /**
     * Occurrences the invoice bills today that a narrowed selection would drop. They go back to
     * non-invoiced billing, so the user is told before saving.
     */
    export function occurrencesLosingBilling(args: {
        invoice: Invoice | undefined;
        candidateRsrvIds: Iterable<number>;
        labels?: ReadonlyMap<number, string>;
    }): { rsrvId: number; label: string }[] {
        if (!args.invoice) return [];

        const candidates = new Set(args.candidateRsrvIds);

        return [...InvoiceMove.invoiceObjectIds(args.invoice).rsrvIds]
            .filter((rsrvId) => {
                // An occurrence with no label belongs to an event this view is not showing, so a
                // selection made here says nothing about whether it keeps its billing.
                if (args.labels && !args.labels.has(rsrvId)) return false;
                return !candidates.has(rsrvId);
            })
            .map((rsrvId) => ({ rsrvId, label: args.labels?.get(rsrvId) ?? "" }));
    }

    /** A range bills from the first moment of its start day through the last of its end day. */
    function rangeFields(range: DateRange) {
        return {
            startDt: S25Util.date.toStartOfDay(S25Util.date.parse(range.start)),
            endDt: S25Util.date.toEndOfDay(S25Util.date.parse(range.end)),
        };
    }

    export type InvoiceFormSelection = {
        name: string;
        eventIds: number[];
        rsrvIds: number[];
        requirementIds: number[];
        includeEventType: boolean;
        /** Set for the date range and include all modes, which bill by range instead of by list. */
        dateRange?: DateRange;
    };

    export function createPayload(selection: InvoiceFormSelection): InvoiceCreateData {
        const { rsrvIds, requirementIds, dateRange } = selection;
        // A range invoice bills whatever falls in the range, so it needs the objects even though no
        // occurrence is listed.
        const billsOccurrences = !!rsrvIds.length || !!dateRange;

        return {
            billName: selection.name,
            eventIdList: selection.eventIds,
            includeEventType: selection.includeEventType,
            includeRequirements: !!requirementIds.length,
            includeResources: billsOccurrences,
            includeSpaces: billsOccurrences,
            ...(!!requirementIds.length && { requirementIdList: requirementIds }),
            ...(!!rsrvIds.length && { rsrvIdList: rsrvIds }),
            ...(dateRange && rangeFields(dateRange)),
        };
    }

    /**
     * Switching an invoice off a date range needs its own request: a startDt sent alongside a
     * reservation list is dropped and the stored range restored, and the empty reservation list flag,
     * the only thing that clears the range, refuses a list in the same request.
     */
    export function clearDateRangePayload(args: {
        invoice: Invoice;
        eventIds: number[];
        rsrvIds: number[];
        dateRange?: DateRange;
    }): Partial<InvoiceUpdateData> | undefined {
        if (args.dateRange || invoiceBillingMode(args.invoice) === "list") return undefined;
        // With nothing picked, the edit's own empty flag clears the range along with the list.
        if (!args.rsrvIds.length) return undefined;
        return { eventIdList: args.eventIds, ...emptyReservationListFields(true) };
    }

    export function editPayload(selection: InvoiceFormSelection & { invoice: Invoice }): Partial<InvoiceUpdateData> {
        const { invoice, rsrvIds, requirementIds, dateRange } = selection,
            hasOccurrences = !!rsrvIds.length,
            hasRequirements = !!requirementIds.length,
            // The displayed name, so leaving the default set's stand-in name alone writes nothing.
            nameChanged = invoiceDisplayName(invoice) !== selection.name;

        return {
            eventIdList: selection.eventIds,
            ...(nameChanged && { billName: selection.name }),
            ...(dateRange
                ? rangeFields(dateRange)
                : {
                      ...emptyReservationListFields(!hasOccurrences),
                      ...(hasOccurrences && { rsrvIdList: rsrvIds }),
                      includeResources: hasOccurrences,
                      includeSpaces: hasOccurrences,
                  }),
            includeRequirements: hasRequirements,
            includeEventType: selection.includeEventType,
            ...(hasRequirements && { requirementIdList: requirementIds }),
        };
    }

    /**
     * An edit that changes only which occurrences are billed. The name, requirements and event type
     * are restated from the invoice so an occurrence-only save leaves them as they stand.
     */
    export function occurrenceListPayload(args: {
        invoice: Invoice;
        eventIds: number[];
        rsrvIds: number[];
    }): Partial<InvoiceUpdateData> {
        const billed = InvoiceMove.invoiceObjectIds(args.invoice);

        return editPayload({
            ...args,
            name: invoiceDisplayName(args.invoice),
            requirementIds: [...billed.reqIds],
            includeEventType: billed.includeEventType,
        });
    }
}
